import React from 'react';
import { useNavigate } from 'react-router-dom';
import { REALMS, TROPHIES, FAM_LABELS } from '../data/realms';
import { getProfileSize } from './Fizzbuzz';
import AvatarImg from './AvatarImg';
import { problemsBySkillId } from '../data/problems';
import { evaluateTrophy, type TrophyInputs } from '../data/trophies';

// Presentational profile body. Pulled out of pages/ProfilePage.tsx so
// pages/PublicProfilePage.tsx can render the same UI from a server response.
// The component is data-driven — sections not present in `data` simply
// collapse, which is how stranger profiles render without heatmap/sparkline.

const HEAT_COLORS = [
  'var(--paper)',
  'var(--paper-3)',
  'var(--w-cream)',
  'var(--w-amber)',
  'var(--w-coral)',
];

const ACCENT: Record<string, string> = {
  amber: 'var(--w-amber)',
  peach: 'var(--w-peach)',
  coral: 'var(--w-coral)',
  tan:   'var(--w-tan)',
  cream: 'var(--w-cream)',
};

function heatBucket(xp: number): number {
  if (xp <= 0) return 0;
  if (xp < 20) return 1;
  if (xp < 50) return 2;
  if (xp < 100) return 3;
  return 4;
}

function relTime(ts: number): string {
  const diff = Math.max(0, Date.now() - ts);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export interface RealmStat {
  id: string;
  name: string;
  emoji: string;
  color: string;
  solved: number;
  total: number;
}

export function buildRealmStats(solvedByTopic: Record<string, number>): RealmStat[] {
  return REALMS.map(realm => {
    let solved = 0;
    let total = 0;
    for (const skill of realm.skills) {
      total += problemsBySkillId(skill.id).length;
      solved += solvedByTopic[skill.id] ?? 0;
    }
    return { id: realm.id, name: realm.name, emoji: realm.emoji, color: realm.color, solved, total };
  });
}

export function evaluateTrophyEarned(
  name: string,
  inputs: TrophyInputs,
  realmStats: RealmStat[],
): boolean {
  if (evaluateTrophy(name, inputs)) return true;
  const finished = realmStats.filter(r => r.total > 0 && r.solved >= r.total);
  switch (name) {
    case 'algo native':      return finished.some(r => r.id === 'algo');
    case 'OOP mayor':        return finished.some(r => r.id === 'oop');
    case 'system architect': return finished.some(r => r.id === 'sys');
    case 'API ambassador':   return finished.some(r => r.id === 'api');
    case 'full-stack founder': return finished.some(r => r.id === 'fs');
    case 'state of mind':    return finished.some(r => r.id === 'react');
    case 'AI wanderer':      return finished.some(r => r.id === 'ai');
    case 'syntax sage':      return finished.some(r => r.id === 'lang');
    case 'graduate':         return finished.length >= 8;
    default: return false;
  }
}

export interface RecentSolve {
  ts: number;
  xp: number;
  problemId: string;
  title: string;
}

export interface ProfileBodyProps {
  // ── identity ──────────────────────────────────────────────────────────
  username: string;
  level: number;
  // ── friend / self gating ─────────────────────────────────────────────
  // 'self' shows the back-to-map CTA + xp/coins pills; 'friend' shows the
  // private sections without the CTA; 'stranger' collapses to public basics.
  view: 'self' | 'friend' | 'stranger' | 'pending_in' | 'pending_out';
  // ── always-public stats ──────────────────────────────────────────────
  streak: number;
  solvedCount: number;
  solvedByTier: { mid: number; cracked: number; goated: number };
  solvedByTopic: Record<string, number>;
  // ── private-only data (provide only for friend/self) ─────────────────
  xp?: number;
  xpToNextLevel?: number;
  xpPercent?: number;
  coins?: number;
  heatDays?: { date: string; xp: number }[];     // 84 cells, oldest first
  sparkDays?: { date: string; xp: number }[];    // 7 cells, oldest first
  recentSolves?: RecentSolve[];
  // ── trophies ─────────────────────────────────────────────────────────
  // For friend/self: full earned-or-not map evaluated upstream. For stranger:
  // pass `trophyCount` only and we render a single summary box.
  trophyEarned?: Record<string, boolean>;
  trophyCount?: number;
  // ── action slot (header-right) ───────────────────────────────────────
  // Right-aligned action button area beside the identity card title. Used by
  // PublicProfilePage for the Send Request / Accept / Unfriend controls.
  actionSlot?: React.ReactNode;
}

const ProfileBody: React.FC<ProfileBodyProps> = (props) => {
  const navigate = useNavigate();
  const {
    username, level, view, streak, solvedCount, solvedByTier, solvedByTopic,
    xp, xpToNextLevel, xpPercent, coins, heatDays, sparkDays, recentSolves,
    trophyEarned, trophyCount, actionSlot,
  } = props;
  const fbSize = getProfileSize(level);

  const realmStats = React.useMemo(() => buildRealmStats(solvedByTopic), [solvedByTopic]);

  const heatTotal = (heatDays ?? []).reduce((a, d) => a + (d.xp > 0 ? 1 : 0), 0);

  // 7-day sparkline polyline coordinates (only computed when sparkDays present).
  const sparkData = React.useMemo(() => {
    if (!sparkDays || sparkDays.length === 0) return [];
    const maxXp = Math.max(1, ...sparkDays.map(d => d.xp));
    return sparkDays.map((d, i) => ({
      x: (i / Math.max(1, sparkDays.length - 1)) * 400,
      y: 80 - (d.xp / maxXp) * 70 - 5,
      xp: d.xp,
      date: d.date,
    }));
  }, [sparkDays]);
  const sparkPoints = sparkData.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`);
  const sparkPolyline = sparkPoints.join(' ');
  const sparkArea = sparkPoints.length > 0 ? `M 0,80 L ${sparkPoints.join(' L ')} L 400,80 Z` : '';

  const showPrivate = view === 'self' || view === 'friend';
  const earnedTotal = trophyEarned ? Object.values(trophyEarned).filter(Boolean).length : (trophyCount ?? 0);
  const trophyFamilies = Array.from(new Set(TROPHIES.map(t => t.fam)));

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--paper-2)' }}>
      <div className="scroll-y" style={{ flex: 1, padding: 16, display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* Identity card */}
        <div className="sk-box shadow" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14 }}>
          <div className="sk-avatar" style={{ width: fbSize, height: fbSize, fontSize: fbSize / 2.5, padding: 0, overflow: 'hidden' }}>
            <AvatarImg size={fbSize} mood="happy" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>@{username}</h1>
              {actionSlot}
            </div>
            <div style={{ color: 'var(--ink-soft)', fontSize: 13, marginTop: 2 }}>
              level {level} · joined 2026
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <span className="sk-pill">🔥 <b>{streak}</b> day streak</span>
              {showPrivate && xp !== undefined && xpToNextLevel !== undefined && (
                <span className="sk-pill amber">⭐ {xp.toLocaleString()} / {xpToNextLevel.toLocaleString()} xp</span>
              )}
              {view === 'self' && coins !== undefined && (
                <span className="sk-pill"><span className="sk-coin">¢</span> <b>{coins}</b></span>
              )}
            </div>
            {showPrivate && xpPercent !== undefined && (
              <div
                className="sk-box tight"
                style={{ height: 8, marginTop: 10, padding: 0, overflow: 'hidden', background: 'var(--paper-2)' }}
                title={`${xpPercent}% to level ${level + 1}`}
              >
                <div style={{ width: `${xpPercent}%`, height: '100%', background: 'var(--w-amber)', transition: 'width 0.4s' }} />
              </div>
            )}
          </div>
        </div>

        {/* Stat tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 12 }}>
          {[
            { v: String(solvedCount), l: 'problems solved', c: 'amber' },
            { v: `${streak} 🔥`,      l: 'current streak',  c: 'coral' },
            { v: String(level),       l: 'current level',   c: 'cream' },
          ].map((s, i) => (
            <div key={i} className={`sk-box shadow ${s.c}`} style={{ padding: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>{s.v}</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, marginTop: 4 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Difficulty breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10 }}>
          {[
            { v: solvedByTier.mid,     l: 'mid',     c: 'cream' },
            { v: solvedByTier.cracked, l: 'cracked', c: 'amber' },
            { v: solvedByTier.goated,  l: 'goated',  c: 'coral' },
          ].map((s, i) => (
            <div key={i} className={`sk-box tight ${s.c}`} style={{ padding: 10, textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{s.v}</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Activity heatmap (friend/self only) */}
        {showPrivate && heatDays && heatDays.length > 0 && (
          <div className="sk-box" style={{ padding: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className="sk-anno">activity · last 12 weeks</div>
              <span style={{ flex: 1 }} />
              <span className="mono-cap">
                {heatTotal} active day{heatTotal === 1 ? '' : 's'}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, marginTop: 8 }}>
              {heatDays.map((d, i) => (
                <div
                  key={i}
                  title={`${d.date} · ${d.xp} xp`}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    background: HEAT_COLORS[heatBucket(d.xp)],
                    border: '0.5px solid var(--ink)',
                    borderRadius: 2,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* XP sparkline (friend/self only) */}
        {showPrivate && sparkDays && (
          <div className="sk-box" style={{ padding: 12, marginTop: 12 }}>
            <div className="sk-anno">xp · last 7 days</div>
            {sparkData.length === 0 || sparkData.every(p => p.xp === 0) ? (
              <div className="mono-cap" style={{ marginTop: 8, fontSize: 11 }}>
                {view === 'self' ? 'solve your first problem to start the streak →' : 'no recent activity'}
              </div>
            ) : (
              <svg viewBox="0 0 400 80" width="100%" height={80} style={{ marginTop: 4 }}>
                <path d={sparkArea} fill="var(--w-amber)" opacity="0.35" />
                <polyline points={sparkPolyline} stroke="var(--w-coral)" strokeWidth="2" fill="none" />
                {sparkData.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r="3" fill="var(--w-coral)" stroke="var(--ink)" strokeWidth="1">
                    <title>{`${p.date}: ${p.xp} xp`}</title>
                  </circle>
                ))}
              </svg>
            )}
          </div>
        )}

        {/* Recent solves (friend/self only) */}
        {showPrivate && recentSolves && recentSolves.length > 0 && (
          <div className="sk-box" style={{ padding: 12, marginTop: 12 }}>
            <div className="sk-anno">recent solves</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
              {recentSolves.map((r, i) => (
                <div
                  key={`${r.problemId}-${r.ts}-${i}`}
                  onClick={() => navigate(`/problem/${r.problemId}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 6px',
                    cursor: 'pointer',
                    borderRadius: 4,
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title}
                  </span>
                  <span className="sk-pill amber" style={{ fontSize: 10 }}>+{r.xp} xp</span>
                  <span className="mono-cap">
                    {relTime(r.ts)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Realm progress */}
        <h2 style={{ margin: '18px 0 8px', fontSize: 17, fontWeight: 700 }}>progress by realm</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {realmStats.map(r => {
            const pct = r.total > 0 ? Math.round((r.solved / r.total) * 100) : 0;
            return (
              <div key={r.id} className="sk-box" style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 24 }}>{r.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{r.name}</div>
                    <div className="mono-cap">
                      {r.solved} / {r.total} · {pct}%
                    </div>
                  </div>
                  <span className={`sk-pill ${r.color}`}>{pct}%</span>
                </div>
                <div className="sk-box tight" style={{ height: 8, marginTop: 8, padding: 0, overflow: 'hidden', background: 'var(--paper-2)' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: ACCENT[r.color] ?? 'var(--w-amber)', transition: 'width 0.4s' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Trophy case */}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 24, marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>trophy case</h2>
          <span className="sk-pill amber" style={{ marginLeft: 10 }}>
            {earnedTotal} / {TROPHIES.length} earned
          </span>
        </div>

        {trophyEarned ? (
          // Full grid (friend / self) — we have the earned-or-not map.
          trophyFamilies.map(fam => {
            const list = TROPHIES.filter(t => t.fam === fam);
            return (
              <div key={fam} style={{ marginBottom: 16 }}>
                <div className="sk-anno" style={{ marginBottom: 8, color: 'var(--w-coral)' }}>
                  {FAM_LABELS[fam] ?? fam}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
                  {list.map((t, i) => {
                    const earned = !!trophyEarned[t.name];
                    return (
                      <div
                        key={i}
                        className={`sk-box tight ${earned ? 'amber' : ''}`}
                        style={{
                          padding: 8,
                          textAlign: 'center',
                          opacity: earned ? 1 : 0.6,
                          backgroundImage: earned
                            ? 'none'
                            : 'repeating-linear-gradient(45deg, transparent 0 6px, rgba(30,27,22,0.18) 6px 7px)',
                          cursor: 'pointer',
                        }}
                        title={t.desc}
                      >
                        <div style={{ fontSize: 26, filter: earned ? 'none' : 'grayscale(0.6)' }}>{t.e}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, marginTop: 4 }}>{t.name}</div>
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: 'var(--ink-faint)', marginTop: 2 }}>
                          {t.desc}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          // Stranger view — total count only, in line with privacy tier.
          <div className="sk-box tight mono-cap" style={{ padding: 12, marginBottom: 16, fontSize: 11 }}>
            trophy details are visible to friends only
          </div>
        )}

        {/* Self-only CTA */}
        {view === 'self' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: 14, marginTop: 8, marginBottom: 24,
            background: 'var(--paper-2)',
            border: '1.4px dashed var(--ink)',
            borderRadius: 12,
          }}>
            <AvatarImg size={48} mood="happy" />
            <div>
              <div style={{ fontWeight: 700 }}>keep going!</div>
              <div className="mono-cap" style={{ marginTop: 2 }}>
                finish today's daily challenge to unlock <b>fortnight phenom</b>
              </div>
            </div>
            <button
              className="sk-btn primary"
              style={{ marginLeft: 'auto' }}
              onClick={() => navigate('/')}
            >
              back to map
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileBody;
